import React, { useState, useRef, useEffect } from "react";
import {
    TextField,
    Button,
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    List,
    ListItem,
    ListItemText,
    Box,
} from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import mapboxgl from "mapbox-gl";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import isMock from "../../config";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {useMaterialUIController} from "../../context";
import Icon from "@mui/material/Icon";

mapboxgl.accessToken =
    "pk.eyJ1IjoibGVvbnRvbWVyIiwiYSI6ImNtNTVjeTdwcTJ3em4yanNkeHg3eDNhaW0ifQ.3Tg0RL9HhK4yQRzVpMMB1Q";

function Places() {
    const [searchTerm, setSearchTerm] = useState("");
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [newPlaceName, setNewPlaceName] = useState("");
    const [newPlaceAddress, setNewPlaceAddress] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [controller] = useMaterialUIController();
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
    const [globalFilter, setGlobalFilter] = useState("");
    const [expanded, setExpanded] = useState({});

    const [debouncedFilter, setDebouncedFilter] = useState("");
    useEffect(() => {
        const t = setTimeout(() => setDebouncedFilter(globalFilter), 400);
        return () => clearTimeout(t);
    }, [globalFilter]);

    const { data, isLoading } = useQuery({
        queryKey: ["palPlacesTree", pagination.pageSize, pagination.pageIndex, debouncedFilter],
        queryFn: async () => {
            const limit = pagination.pageSize;
            const skip = pagination.pageIndex * pagination.pageSize;
            const url =
                `https://blue-parking-invite.bluept.dev/api/pal-places/tree` +
                `?limit=${limit}&skip=${skip}&filter=${encodeURIComponent(debouncedFilter || "")}`;

            const res = await fetch(url);
            if (!res.ok) throw new Error("Failed to fetch places");
            return res.json();
        },
    });

    const places = data?.places?.list ?? [];
    const rowCount = data?.places?.count ?? 0;
    const { darkMode } = controller;
    const textColor = darkMode ? "#fff" : "#000";
    const cardBgColor = darkMode ? "#1e293b" : "#fff";
    // New states for Delete Confirmation
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedPlaceToDelete, setSelectedPlaceToDelete] = useState(null);

    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);

    // React Query Client
    const queryClient = useQueryClient();
    const collectExpandableParents = (nodes, out = {}) => {
        for (const n of nodes || []) {
            if (n?.subPlaces?.length) {
                out[n.id] = true; // expand this parent
                collectExpandableParents(n.subPlaces, out);
            }
        }
        return out;
    };

    useEffect(() => {
        // only auto-expand when searching (so user can still collapse normally in normal mode)
        if (debouncedFilter?.trim()) {
            setExpanded(collectExpandableParents(places, {}));
        } else {
            setExpanded({}); // collapse back when search cleared (optional)
        }
    }, [debouncedFilter, places]);
    // Fetch function for places
    const fetchPlaces = async ({ queryKey }) => {
        const [, pageSize] = queryKey; // ["palPlacesTree", pageSize]
        const response = await fetch(`https://blue-parking-invite.bluept.dev/api/pal-places/tree?limit=${pageSize}`);
        if (!response.ok) throw new Error("Failed to fetch PAL places tree");
        const data = await response.json();
        return data.places || [];
    };
    //
    // // useQuery to get places data
    // const { data: places = [], isLoading, error } = useQuery({
    //     queryKey: ["palPlacesTree", pagination.pageSize],
    //     queryFn: fetchPlaces,
    //     keepPreviousData: true,
    // });

    // useMutation to add a new place
    const addPlaceMutation = useMutation({
        mutationFn: async (newPlaceData) => {
            const response = await fetch("https://blue-parking-invite.bluept.dev/api/pal-places", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(newPlaceData),
            });
            if (!response.ok) throw new Error("Failed to add new place");
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["palPlacesTree"]);
        },
    });

    // useMutation to delete a place
    const deletePlaceMutation = useMutation({
        mutationFn: async (id) => {
            const response = await fetch(
                `https://blue-parking-invite.bluept.dev/api/pal-places/${id}`, // ✅ new
                { method: "DELETE" }
            );
            const data = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(data?.error || "Failed to delete place");
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["palPlacesTree"]);
        },
    });


    useEffect(() => {
        console.log(places);
    }, [places]);


    const expandOnlyMatchPaths = (nodes, q) => {
        const query = (q ?? "").toString().toLowerCase().trim();
        const expandedMap = {};

        const walk = (node) => {
            const name = (node?.name ?? "").toString().toLowerCase();
            const address = (node?.address ?? "").toString().toLowerCase();

            const selfMatch = query && (name.includes(query) || address.includes(query));

            let childHasMatch = false;
            for (const ch of node?.subPlaces ?? []) {
                if (walk(ch)) childHasMatch = true;
            }

            // if any child matches, expand this node so the child becomes visible
            if (childHasMatch) expandedMap[node.id] = true;

            // return whether this subtree contains a match
            return selfMatch || childHasMatch;
        };

        for (const n of nodes ?? []) walk(n);

        return expandedMap;
    };

    useEffect(() => {
        const q = debouncedFilter?.trim();
        if (!q) {
            setExpanded({});
            return;
        }

        setExpanded(expandOnlyMatchPaths(places, q));
    }, [debouncedFilter, places]);

    const columns = [
        {
            accessorKey: "name",
            header: "Name",
            Cell: ({ row }) => {
                const depth = row.depth || 0;

                return (
                    <Box sx={{ display: "flex", alignItems: "center", pl: `${depth * 18}px` }}>
                        {/* optional small marker to emphasize child rows */}
                        {depth > 0 && (
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    mr: 1,
                                    opacity: 0.8,
                                    backgroundColor: darkMode ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.35)",
                                }}
                            />
                        )}

                        <a
                            href={`/places/${row.original.id}`}
                            style={{
                                color: darkMode ? "#7DD3FC" : "#0B63CE",
                                textDecoration: "underline",
                                cursor: "pointer",
                                fontWeight: depth === 0 ? 700 : 500,
                            }}
                        >
                            {row.original.name}
                        </a>
                    </Box>
                );
            },
        },
        {
            accessorKey: "address_name",
            header: "Address",
            filterFn: (row, columnId, filterValue) => searchRowRecursively(row, columnId, filterValue),
        },
        // NEW ACTION COLUMN FOR DELETION
        {
            id: "delete",
            header: "Delete",
            Cell: ({ row }) => (
                <Button
                    // variant="contained"
                    color="error"
                    onClick={() => handleOpenDeleteDialog(row.original.id)}
                >
                    <Icon>delete</Icon>
                </Button>
            ),
        },
    ];

    // Functions for handling the delete dialog
    const handleOpenDeleteDialog = (placeId) => {
        setSelectedPlaceToDelete(placeId);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setSelectedPlaceToDelete(null);
        setDeleteDialogOpen(false);
    };

    const handleConfirmDelete = () => {
        if (selectedPlaceToDelete) {
            deletePlaceMutation.mutate(selectedPlaceToDelete);
        }
        handleCloseDeleteDialog();
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setNewPlaceName("");
        setNewPlaceAddress("");
        setSuggestions([]);
        setSelectedLocation(null);
    };

    const handleAddPlace = () => {
        if (newPlaceName && newPlaceAddress && selectedLocation) {
            addPlaceMutation.mutate({
                name: newPlaceName,
                address_name: newPlaceAddress,              // matches your tree route field
                // longitude: selectedLocation.longitude,
                // latitude: selectedLocation.latitude,
                parent_id: null, // later you can set this from a "parent place" picker
            });
        }
        handleDialogClose();
    };



    const theme = createTheme({
        components: {
            MuiTablePagination: {
                styleOverrides: {
                    root: {
                        backgroundColor: darkMode ? "#1e293b" : "#f5f5f5",
                        color: darkMode ? "#fff" : "#000",
                    },
                    selectIcon: {
                        color: darkMode ? "#fff" : "#000",
                    },
                },
            },
            MuiSvgIcon: {
                styleOverrides: {
                    root: {
                        color: darkMode ? "#fff" : "#000",
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        color: darkMode ? "#fff" : "#000",
                    },
                },
            },
        },
    });

    const handleAddressChange = (address) => {
        setNewPlaceAddress(address);
        fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
                address
            )}.json?access_token=${mapboxgl.accessToken}`
        )
            .then((res) => res.json())
            .then((data) => {
                if (data.features) {
                    setSuggestions(
                        data.features.map((feature) => ({
                            text: feature.place_name,
                            coordinates: feature.geometry.coordinates,
                        }))
                    );
                }
            })
            .catch((error) => console.error("Geocoding error:", error));
    };

    const handleSuggestionClick = (suggestion) => {
        setNewPlaceAddress(suggestion.text);
        setSelectedLocation({
            longitude: suggestion.coordinates[0],
            latitude: suggestion.coordinates[1],
        });
        setSuggestions([]);

        if (mapRef.current) {
            mapRef.current.flyTo({ center: suggestion.coordinates, zoom: 14 });
            new mapboxgl.Marker().setLngLat(suggestion.coordinates).addTo(mapRef.current);
        }
    };

    const handleDialogEntered = () => {
        if (!mapRef.current && mapContainerRef.current) {
            mapRef.current = new mapboxgl.Map({
                container: mapContainerRef.current,
                style: "mapbox://styles/mapbox/streets-v11",
                center: [0, 0],
                zoom: 2,
            });
        } else {
            mapRef.current?.resize();
        }
    };

    const handleDialogExited = () => {
        if (mapRef.current) {
            mapRef.current.remove();
            mapRef.current = null;
        }
    };

    const searchRowRecursively = (row, columnId, filterValue) => {
        const value = row.getValue(columnId);
        if (value?.toLowerCase().includes(filterValue.toLowerCase())) {
            return true;
        }
        if (row.subPlaces?.length) {
            return row.subPlaces.some((subRow) => searchRowRecursively(subRow, columnId, filterValue));
        }
        return false;
    };

    const rowMatchesRecursive = (row, filterValue) => {
        const q = (filterValue ?? "").toString().toLowerCase().trim();
        if (!q) return true;

        const name = (row.original?.name ?? "").toString().toLowerCase();
        const address = (row.original?.address ?? "").toString().toLowerCase();

        // match current row
        if (name.includes(q) || address.includes(q)) return true;

        // match any descendant rows (MRT stores them in row.subRows)
        if (row.subRows?.length) {
            return row.subRows.some((r) => rowMatchesRecursive(r, q));
        }

        return false;
    };



    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox pt={6} pb={3} px={2}>
                {/* Header with Add Button */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <MDTypography variant="h4">Places</MDTypography>
                    <Button
                        variant="contained"
                        onClick={() => setIsDialogOpen(true)}
                        sx={{
                            backgroundColor: "#1976d2",
                            color: "#fff",
                            "&:hover": { backgroundColor: "#115293" },
                        }}
                    >
                        <Icon>add</Icon>
                    </Button>
                </Box>

                {/* MaterialReactTable */}
                <ThemeProvider theme={theme}>
                    <MaterialReactTable
                        columns={columns}
                        data={places}
                        manualPagination
                        manualFiltering
                        rowCount={rowCount}
                        getRowId={(row) => row.id}
                        onGlobalFilterChange={setGlobalFilter}
                        state={{
                            pagination,
                            globalFilter,
                            isLoading,
                            expanded,
                        }}
                        onExpandedChange={setExpanded}
                        enableExpanding
                        onPaginationChange={setPagination}
                        getSubRows={(row) => row?.subPlaces}
                        enableGlobalFilter
                        muiBottomToolbarProps={{
                            sx: {
                                backgroundColor: darkMode ? '#1e293b' : '#fff',
                                color: darkMode ? '#fff' : '#1e293b',
                                ".MuiFormLabel-root": {
                                    color: darkMode ? '#fff' : '#1e293b',
                                },
                                ".MuiSelect-icon": {
                                    color: darkMode ? '#fff' : '#1e293b',
                                },
                                ".MuiSelect-select": {
                                    color: darkMode ? '#fff' : '#1e293b',
                                },
                                ".MuiBox-root.MuiButtonBase-root" : {
                                    color: darkMode ? '#fff' : '#1e293b',
                                },
                                ".MuiSvgIcon-root" : {
                                    color: darkMode ? '#fff' : '#1e293b',
                                },
                                '& .MuiButtonBase-root.Mui-disabled .MuiSvgIcon-root': {
                                    // pick a dimmer color for disabled icons
                                    color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                },
                            }
                        }}
                        muiTopToolbarProps={{
                            sx: {
                                backgroundColor: darkMode ? '#1e293b' : '#fff',

                                borderBottom: darkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                                "& .MuiInputBase-root": {
                                    border: darkMode ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.15)",
                                    borderRadius: "10px",
                                    px: 1,
                                },
                                "& .MuiInputBase-input": {
                                    color: darkMode ? "#fff" : "#1e293b",
                                },
                            },
                        }}
                        muiTableBodyCellProps={{
                            sx: {
                                backgroundColor: darkMode ? "#1e293b" : "#ffffff",
                                color: darkMode ? "#ffffff" : "#000",
                                ".MuiSvgIcon-root" : {
                                    color: darkMode ? "#ffffff" : "#000",
                                }
                            },
                        }}
                        muiTableHeadCellProps={{
                            sx: {
                                backgroundColor: darkMode ? "#1e293b" : "#0098CF",
                                color: "white",
                                fontWeight: "bold",
                                ".MuiSvgIcon-root" : {
                                    color: darkMode ? "#ffffff" : "#000",
                                },
                                // then specifically target the sort label icon:
                                '& .MuiTableSortLabel-icon': {
                                    color: darkMode ? '#fff !important' : '#000 !important',
                                },
                                // likewise for iconDirectionAsc / iconDirectionDesc if needed
                                '& .MuiTableSortLabel-iconDirectionAsc': {
                                    color: darkMode ? '#fff !important' : '#000 !important',
                                },
                                '& .MuiTableSortLabel-iconDirectionDesc': {
                                    color: darkMode ? '#fff !important' : '#000 !important',
                                },
                                '& .MuiButtonBase-root.Mui-disabled .MuiSvgIcon-root': {
                                    // pick a dimmer color for disabled icons
                                    color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                },
                                '& .MuiInputBase-input': {
                                    color: darkMode ? '#fff' : '#1e293b',
                                }
                            },
                        }}
                        // globalFilterFn="recursiveSearch"
                        // filterFns={{
                        //     recursiveGlobal: (row, _columnId, filterValue) => rowMatchesRecursive(row, filterValue),
                        // }}
                        // Style the top toolbar (the area containing search, column toggles, etc.)
                        muiTableHeadProps={{
                            sx: {
                                display: "table-header-group",
                                backgroundColor: "rgb(0,0,0)",
                                color: "white",
                            },
                        }}
                        initialState={{
                            expanded: false,
                        }}
                        muiTableBodyRowProps={({ row }) => ({
                            sx: {
                                backgroundColor: darkMode
                                    ? row.index % 2 === 0 ? "rgba(255,255,255,0.03)" : "transparent"
                                    : row.index % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent",
                                "&:hover td": {
                                    backgroundColor: darkMode ? "rgba(125, 211, 252, 0.10)" : "rgba(11, 99, 206, 0.08)",
                                },
                            },
                        })}
                        muiTableContainerProps={{
                            sx: {
                                width: "100%",
                                overflowX: "auto",
                                backgroundColor: darkMode ? "#0B1220" : "#ffffff",
                                borderRadius: "12px",
                            },
                        }}

                    />
                </ThemeProvider>
            </MDBox>

            {/* Dialog for Adding a New Place */}
            <Dialog
                open={isDialogOpen}
                onClose={handleDialogClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: cardBgColor,
                        color: textColor, // so text inherits this color by default
                    },
                }}
                TransitionProps={{
                    onEntered: handleDialogEntered,
                    onExited: handleDialogExited,
                }}
            >
                <DialogTitle style={{ color: textColor }}>Add New Place</DialogTitle>
                <DialogContent sx={{ display: "flex", gap: 2 }}>
                    <div style={{ flex: 1 }}>
                        <TextField
                            fullWidth
                            label="Place Name"
                            variant="outlined"
                            value={newPlaceName}
                            onChange={(e) => setNewPlaceName(e.target.value)}
                            sx={{
                                mb: 2,
                                mt: 2,
                                "& .MuiInputLabel-root": {
                                    color: textColor,
                                },
                                "& .MuiInputBase-input": {
                                    color: textColor,
                                },
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Place Address"
                            variant="outlined"
                            sx={{
                                "& .MuiInputLabel-root": {
                                    color: textColor,
                                },
                                "& .MuiInputBase-input": {
                                    color: textColor,
                                },
                            }}
                            value={newPlaceAddress}
                            onChange={(e) => handleAddressChange(e.target.value)}
                        />

                        <List>
                            {suggestions.map((suggestion, index) => (
                                <ListItem button key={index} onClick={() => handleSuggestionClick(suggestion)}>
                                    <ListItemText style={{ color: textColor }} primary={suggestion.text} />
                                </ListItem>
                            ))}
                        </List>
                    </div>

                    <div
                        key={isDialogOpen ? "map-open" : "map-closed"}
                        style={{ flex: 1, height: "400px", marginTop: 16 }}
                        ref={mapContainerRef}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="secondary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAddPlace}
                        color="primary"
                        disabled={!newPlaceName || !newPlaceAddress}
                    >
                        Add Place
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog for Deletion Confirmation */}
            <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
                <DialogTitle sx={{color: "#000"}}>Delete Confirmation</DialogTitle>
                <DialogContent sx={{color: "#000"}}>
                    Are you sure you want to delete this place?
                </DialogContent>
                <DialogActions sx={{display: "flex", justifyContent: "flex-start"}}>
                    <Button onClick={handleCloseDeleteDialog} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="primary">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Footer />
        </DashboardLayout>
    );
}

export default Places;
